import React from 'react';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { some } from 'lodash';
import { useRouter } from 'next/router';

import { Event } from 'legacy/common/types/models';
import {
  useCreateRecurringSeries,
  useGetEvent,
  useGetEventRecurringSeries,
  useGetEventTiers,
  useUpdateRecurringSeries
} from 'legacy/events/hooks';
import * as toast from 'legacy/common/utils/toast';
import Loader from 'legacy/common/components/layout/Loader';
import { isBounceWebview, postMessageToRN } from 'legacy/common/utils/webview';
import { UploadAspectRatios } from 'legacy/common/components/wysiwyg-form/ImageUpload';
import { WEBVIEW_CLOSE } from 'legacy/common/constants/webview';

import RecurringSettingsForm from './settings/forms/RecurringSettingsForm';

interface Props {
  eventId: string;
  eventSeriesId?: string;
  navigateToBasicForm: () => void;
  createDraft: (values?: any) => Promise<Event>;
}

const RecurringSettingsSchema = Yup.object().shape({
  startDate: Yup.date().required('Start date is required'),
  repeats: Yup.string(),
  endsAfter: Yup.string()
});

const RecurringSettings: React.FC<Props> = ({
  eventId,
  eventSeriesId,
  createDraft,
  navigateToBasicForm
}) => {
  const router = useRouter();
  const { values: initialValues } = useFormikContext<any>();
  const { data: eventData } = useGetEvent(eventId);
  const { data } = useGetEventTiers(eventId);
  const tiers = data?.items || [];

  const { data: recurringData, isLoading } = useGetEventRecurringSeries(
    { eventId },
    { enabled: !!eventId && !!eventSeriesId }
  );
  const { mutateAsync: createRecurring, isLoading: isCreatingSeries } =
    useCreateRecurringSeries();
  const { mutateAsync: updateRecurring, isLoading: isUpdatingSeries } =
    useUpdateRecurringSeries();

  const showTierDatesWarning = some(
    tiers,
    (tier) => tier.startDate || tier.endDate
  );

  const showSuccessMessage = (text) => {
    toast.showRichMessage({
      image: {
        url: eventData.photo.url,
        aspectRatio: UploadAspectRatios.FourToFive
      },
      text,
      link: {
        text: 'Go to drafts',
        ...(isBounceWebview(router.query)
          ? {
              onClick: () => {
                postMessageToRN(WEBVIEW_CLOSE);
              }
            }
          : { href: '/host' })
      }
    });
  };

  const onSubmit = async (values) => {
    if (!eventSeriesId) {
      try {
        const createResult = await createRecurring({
          eventId,
          startDate: values.startDate,
          seriesEndDate: values.endsAfter,
          frequency: values.repeats
        });

        if (createResult.seriesId) {
          showSuccessMessage(
            createResult.eventIds.length === 1
              ? `1 new draft of ${eventData.title} has been created.`
              : `${createResult.eventIds.length} new drafts of ${eventData.title} have been created.`
          );
          navigateToBasicForm();
        } else {
          toast.error('Error creating series. Please try again later');
        }
      } catch (error) {
        toast.error(error.message || 'Error creating series');
      }
    } else {
      try {
        const updateResult = await updateRecurring({
          eventId,
          startDate: values.startDate,
          seriesEndDate: values.endsAfter || undefined,
          frequency: values.repeats || undefined
        });

        if (updateResult.seriesId) {
          showSuccessMessage(`${eventData.title} series updated successfully`);

          if (
            updateResult.eventIds.includes(eventId) &&
            values.repeats &&
            values.endsAfter
          ) {
            navigateToBasicForm();
          } else {
            // This case seems valid for both the Webview (mobile app) context and Web App context
            navigateToBasicForm();
            router.replace(`/events/${updateResult.eventIds[0]}/?view=edit`);
          }
        } else {
          toast.error('Error updating series. Please try again later');
        }
      } catch (error) {
        toast.error(error.message || 'Error updating series');
      }
    }
  };

  return (
    <>
      {isLoading ? (
        <Loader isLoading />
      ) : (
        <Formik
          initialValues={{
            startDate: recurringData?.startDate || initialValues.startDate,
            repeats: recurringData?.frequency || '',
            endsAfter: recurringData?.endDate || ''
          }}
          onSubmit={onSubmit}
          enableReinitialize
          validationSchema={RecurringSettingsSchema}
        >
          <RecurringSettingsForm
            eventId={eventId}
            canEdit={recurringData?.canEdit}
            initialEventStartDate={initialValues.startDate}
            createDraft={createDraft}
            isLoading={isCreatingSeries || isUpdatingSeries}
            seriesId={recurringData?.seriesId}
            initialSeriesStartDate={recurringData?.startDate}
            initialSeriesEndDate={recurringData?.endDate}
            showTierDatesWarning={showTierDatesWarning}
          />
        </Formik>
      )}
    </>
  );
};

export default RecurringSettings;
